'use client';

import { Popover as PopoverPrimitive } from 'radix-ui';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import {
  ArrowDataTransferVerticalIcon,
  Search02Icon,
  Tick02Icon,
} from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Why this component does NOT use cmdk + the standard `<Popover>` wrapper.
//
// SearchableSelect is rendered inside `<Dialog>` (e.g. the create-proposal
// dialog). Radix Dialog wraps its content in `react-remove-scroll`, which
// adds wheel/touchmove handlers to `document` at CAPTURE phase to block
// every scroll that originates outside its `lockRef`. The popover is
// portaled to `document.body` (a SIBLING of the dialog), so wheel events
// inside the popover's scrollable list are `preventDefault()`-ed before
// they reach the list. Visually it's clipped at `max-h-80`; the user just
// can't wheel/trackpad scroll. Keyboard arrow keys still work because
// they use `scrollIntoView`, which is unaffected.
//
// The trick: Radix's lock listens at `document` capture. `window` capture
// runs BEFORE `document` capture in the bubble path. So we register our
// own `wheel`/`touchmove` listeners at `window` capture while the popover
// is open and call `event.stopPropagation()` for any event whose target
// is a descendant of the popover content. This prevents Radix's
// document-level handler from running and `preventDefault()`-ing the
// scroll, while keeping the popover portaled to body (so it isn't
// clipped by the dialog's `transform` + `overflow-y-auto` containing
// block — a transformed ancestor is a containing block for `position:
// fixed` descendants).
//
// We also skip cmdk and roll a vanilla filtered list with a plain
// scrollable `<div>`. cmdk's `Command.List` introduces a `cmdk-list-sizer`
// wrapper plus a ResizeObserver, and the layered Tailwind / shadcn /
// app-command class merging has been a recurring source of CSS collisions
// in this codebase. A plain `<div>` with explicit `style={{ maxHeight,
// overflowY: 'auto' }}` removes that whole class of failure modes — we
// only have ~50 options at most.
// ---------------------------------------------------------------------------

interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onValueChange: (value: string | null) => void;
  placeholder: string;
  allLabel: string;
  emptyLabel?: string;
  showAllOption?: boolean;
  className?: string;
}

const LIST_MAX_HEIGHT_PX = 320;

function normalize(s: string): string {
  return s.toLowerCase().trim();
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder,
  allLabel,
  emptyLabel = 'No results found.',
  showAllOption = true,
  className,
}: SearchableSelectProps): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Search-input ref so we can focus it imperatively when the popover
  // opens. We don't use the JSX `autoFocus` attribute because Biome's
  // `lint/a11y/noAutofocus` rule (correctly) bans it for arbitrary
  // elements; programmatic focus tied to the popover-open transition is
  // the canonical workaround and gives identical UX.
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // Ref to the popover content element so the window-capture wheel/touch
  // handler (see effect below) can determine whether an event targets
  // our popover and short-circuit Radix Dialog's `react-remove-scroll`.
  const popoverContentRef = useRef<HTMLDivElement | null>(null);

  // Reset the search query whenever the popover closes so reopening
  // starts fresh. On open, focus the search input.
  useEffect((): void => {
    if (!open) {
      setQuery('');
      return;
    }
    // Focus on the next tick so the popover content has mounted.
    queueMicrotask((): void => {
      searchInputRef.current?.focus();
    });
  }, [open]);

  // While the popover is open, intercept wheel/touchmove at WINDOW capture
  // (which runs before DOCUMENT capture) so we can stop propagation for
  // events that target our popover content. Without this, when the
  // SearchableSelect is rendered inside a Radix Dialog, `react-remove-scroll`
  // installs a document-capture handler that calls `preventDefault()` on
  // every wheel/touchmove originating outside the dialog's lockRef — and
  // since our popover is portaled to `document.body` (a sibling of the
  // dialog), it counts as "outside". That blocks scroll inside the option
  // list. Stopping propagation here means Radix's listener never runs.
  // Outside a Dialog the listeners are a harmless no-op.
  useEffect((): (() => void) | undefined => {
    if (!open) return undefined;
    function handler(event: WheelEvent | TouchEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popoverContentRef.current?.contains(target) === true) {
        event.stopPropagation();
      }
    }
    const options: AddEventListenerOptions = { capture: true, passive: false };
    window.addEventListener('wheel', handler, options);
    window.addEventListener('touchmove', handler, options);
    return (): void => {
      window.removeEventListener('wheel', handler, options);
      window.removeEventListener('touchmove', handler, options);
    };
  }, [open]);

  const selectedLabel = value !== null ? options.find((o) => o.value === value)?.label : null;

  const normalizedQuery = normalize(query);
  const filteredOptions: SearchableSelectOption[] =
    normalizedQuery === ''
      ? options
      : options.filter((o) => normalize(o.label).includes(normalizedQuery));

  const showEmpty = query !== '' && filteredOptions.length === 0;

  function handleSelect(next: string | null): void {
    onValueChange(next);
    setOpen(false);
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <AnimatedButton
          variant="secondary"
          disableHoverScale
          aria-expanded={open}
          className={cn(
            'justify-between font-normal border border-border-primary bg-background-primary hover:bg-background-tertiary/50',
            className,
          )}
        >
          <span className="truncate">{selectedLabel ?? allLabel}</span>
          <Icon
            icon={ArrowDataTransferVerticalIcon}
            className="ml-1 size-3.5 shrink-0 opacity-50"
          />
        </AnimatedButton>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={popoverContentRef}
          align="start"
          sideOffset={4}
          className={cn(
            'z-popover w-auto min-w-[var(--radix-popover-trigger-width)] origin-(--radix-popover-content-transform-origin)',
            'overflow-hidden rounded-overlay border bg-surface-floating text-foreground-primary shadow-elevation-popover will-change-transform outline-hidden',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
          )}
        >
          <div className="flex h-9 items-center gap-2 border-b px-3">
            <Icon icon={Search02Icon} className="size-4 shrink-0 opacity-50" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => setQuery(e.target.value)}
              placeholder={placeholder}
              aria-label={placeholder}
              className="flex h-10 w-full rounded-overlay-item bg-transparent py-3 text-body outline-hidden placeholder:text-foreground-tertiary"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div
            role="listbox"
            className="overflow-y-auto overscroll-contain p-1"
            style={{ maxHeight: LIST_MAX_HEIGHT_PX }}
          >
            {showEmpty && (
              <div className="py-6 text-center text-body text-foreground-secondary">
                {emptyLabel}
              </div>
            )}
            {!showEmpty && showAllOption && query === '' && (
              <SearchableSelectRow
                label={allLabel}
                selected={value === null}
                onSelect={(): void => handleSelect(null)}
              />
            )}
            {!showEmpty &&
              filteredOptions.map((option) => (
                <SearchableSelectRow
                  key={option.value}
                  label={option.label}
                  selected={value === option.value}
                  onSelect={(): void => handleSelect(option.value === value ? null : option.value)}
                />
              ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

interface SearchableSelectRowProps {
  label: string;
  selected: boolean;
  onSelect: () => void;
}

function SearchableSelectRow({
  label,
  selected,
  onSelect,
}: SearchableSelectRowProps): React.ReactNode {
  return (
    // ALLOW-RAW-BUTTON: list-row inside a popover — AnimatedButton's variant chrome and motion identity is wrong here; we want a flat list option row.
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-overlay-item px-2 py-1.5 text-body outline-hidden',
        'cursor-default select-none transition-colors',
        'hover:bg-background-tertiary focus-visible:bg-background-tertiary',
        selected && 'bg-background-tertiary text-foreground-primary',
      )}
    >
      <span className="truncate text-left">{label}</span>
      <Icon
        icon={Tick02Icon}
        className={cn('ml-2 size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
      />
    </button>
  );
}
